import { useState } from "react";
import { Layout, Menu, Avatar, Dropdown, Typography, Space, Button } from "antd";
import {
  DashboardOutlined,
  BookOutlined,
  TeamOutlined,
  BarChartOutlined,
  ShoppingOutlined,
  SafetyOutlined,
  FileTextOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  HomeOutlined,
  RocketOutlined,
  CalendarOutlined,
  ApartmentOutlined,
  EditOutlined,
  DollarOutlined,
  UsergroupAddOutlined,
  RobotOutlined,
  IdcardOutlined,
  LineChartOutlined,
  ApiOutlined,
  LockOutlined,
  FormOutlined,
  NotificationOutlined,
  FlagOutlined,
  OrderedListOutlined,
  AuditOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";
import { NotificationBell } from "@/components/NotificationBell";
import { PermissionGate } from "./PermissionGate";
import { hasAction } from "./usePermissions";
import type { AdminAction } from "./types";

const { Header, Content, Sider } = Layout;
const { Text } = Typography;

interface MenuEntry {
  key: string;
  label: string;
  icon: React.ReactNode;
  perm?: AdminAction;
  /** Ẩn theo feature flag app-level (vd TT 83 C1-c identity_ekyc). */
  hidden?: boolean;
}

/**
 * AdminLayout — sider + topbar shell cho /admin/*.
 *
 * Sprint 8A B1: dùng AntD Layout thuần. Add @ant-design/pro-layout nếu cần
 * multi-tab / breadcrumb sâu hơn ở B2+.
 *
 * Menu items ẩn theo permission (L3 RBAC). Toàn bộ /admin/* đã qua AdminRouteGuard
 * (L2). BE permission_classes là L1.
 */
export function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Mỗi entry có optional `perm` — ẩn nếu user không có. Dashboard luôn show.
  const menuEntries: MenuEntry[] = [
    { key: "/admin", label: "Tổng quan", icon: <DashboardOutlined /> },
    { key: "/admin/courses", label: "Môn học", icon: <BookOutlined />, perm: "course.edit" },
    // 2026-10-01 — ẨN menu AI Lesson Designer (Sếp chốt). Route /admin/designer vẫn còn;
    // tạo bài tập / bài kiểm tra bằng AI chuyển sang nút "Tạo bằng AI" ở danh sách bài.
    {
      key: "/admin/curriculum",
      label: "Chương trình đào tạo",
      icon: <ApartmentOutlined />,
      perm: "curriculum.manage",
    },
    { key: "/admin/users", label: "Người dùng", icon: <TeamOutlined />, perm: "user.edit" },
    {
      key: "/admin/users/bulk-import",
      label: "Gán ngành hàng loạt",
      icon: <UsergroupAddOutlined />,
      perm: "user.bulk_import",
    },
    {
      key: "/admin/term-offerings",
      label: "Kỳ học",
      icon: <CalendarOutlined />,
      perm: "term_offering.manage",
    },
    {
      key: "/admin/tuition-rates",
      label: "Học phí/tín chỉ",
      icon: <DollarOutlined />,
      perm: "tuition_rate.manage",
    },
    {
      key: "/admin/grading",
      label: "Chấm bài",
      icon: <EditOutlined />,
      perm: "answer.grade",
    },
    {
      key: "/admin/analytics",
      label: "Phân tích",
      icon: <BarChartOutlined />,
      perm: "analytics.read",
    },
    {
      key: "/admin/orders",
      label: "Đơn hàng",
      icon: <ShoppingOutlined />,
      perm: "order.read",
    },
    {
      key: "/admin/proctor",
      label: "Giám sát thi",
      icon: <SafetyOutlined />,
      // Sprint 6 đã có — share existing route
    },
    {
      // TT 83 C2-d — lưu vết quá trình học (GV: môn mình phụ trách)
      key: "/admin/learning-activity",
      label: "Tiến độ học tập",
      icon: <LineChartOutlined />,
      perm: "course.edit",
    },
    {
      // TT 83 C7-b — yêu cầu của chủ thể dữ liệu (NĐ 13)
      key: "/admin/data-requests",
      label: "Yêu cầu dữ liệu cá nhân",
      icon: <LockOutlined />,
      perm: "curriculum.manage",
    },
    {
      // TT 83 C10 — bảo vệ khoá luận từ xa (tạo phiên: tenant_admin / GV phụ trách)
      key: "/admin/defenses",
      label: "Bảo vệ khoá luận",
      icon: <VideoCameraOutlined />,
      perm: "course.edit",
      hidden: !user?.feature_flags?.live_defense,
    },
    {
      // Plan 2026-10-03 — thông báo chung (tenant_admin: trường/ngành/môn; GV: môn mình)
      key: "/admin/announcements",
      label: "Thông báo chung",
      icon: <NotificationOutlined />,
      perm: "announcement.send",
      hidden: !user?.feature_flags?.notifications,
    },
    {
      // Plan 2026-10-03 — phúc khảo điểm (GV phụ trách môn / tenant_admin)
      key: "/admin/grade-appeals",
      label: "Phúc khảo điểm",
      icon: <AuditOutlined />,
      perm: "appeal.review",
      hidden: !user?.feature_flags?.grade_appeals,
    },
    {
      // Plan 2026-10-03 — rubric chấm điểm (tiêu chí × mức)
      key: "/admin/rubrics",
      label: "Rubric chấm điểm",
      icon: <OrderedListOutlined />,
      perm: "rubric.manage",
      hidden: !user?.feature_flags?.rubric,
    },
    {
      // Plan 2026-10-03 — hàng đợi báo cáo vi phạm diễn đàn
      key: "/admin/forum-reports",
      label: "Báo cáo diễn đàn",
      icon: <FlagOutlined />,
      perm: "forum.moderate",
      hidden: !user?.feature_flags?.forum,
    },
    {
      // TT 83 C9 — khảo sát hài lòng 5.4 (tenant_admin)
      key: "/admin/surveys",
      label: "Khảo sát hài lòng",
      icon: <FormOutlined />,
      perm: "curriculum.manage",
      hidden: !user?.feature_flags?.surveys,
    },
    {
      // TT 83 C9-d — GV xem kết quả khảo sát về mình (tổng hợp, ẩn khi ít phiếu)
      key: "/admin/survey-results",
      label: "Kết quả khảo sát",
      icon: <FormOutlined />,
      hidden: !user?.feature_flags?.surveys || user?.role !== "instructor",
    },
    // 2026-10-01 — ẨN menu "Kiểm định" (Sếp chốt). Route /admin/accreditation vẫn còn.
    {
      // TT 83 C6 — HEMIS + SIS (tenant_admin, dữ liệu chứa CCCD)
      key: "/admin/integration",
      label: "Kết nối dữ liệu",
      icon: <ApiOutlined />,
      perm: "curriculum.manage",
      hidden: !user?.feature_flags?.integration_hemis && !user?.feature_flags?.integration_sis,
    },
    {
      // TT 83 C5 — chính sách AI cấp trường (tenant_admin)
      key: "/admin/ai-policy",
      label: "Chính sách AI",
      icon: <RobotOutlined />,
      perm: "curriculum.manage",
      hidden: !user?.feature_flags?.ai_governance,
    },
    {
      // TT 83 C1-c/d — duyệt eKYC (tenant_admin) + đối chiếu khi thi (GV coi thi).
      key: "/admin/identity",
      label: "Xác thực danh tính",
      icon: <IdcardOutlined />,
      hidden: !user?.feature_flags?.identity_ekyc,
    },
    {
      key: "/admin/audit",
      label: "Nhật ký",
      icon: <FileTextOutlined />,
      perm: "audit.read",
    },
    // Sprint 24 B5 — Career Connect admin (interest list + status PATCH + CSV export)
    {
      key: "/admin/careers/interests",
      label: "Việc làm",
      icon: <RocketOutlined />,
      perm: "audit.read",
    },
  ];

  // Build menu items applying permission filter — use `usePermissions` directly để khỏi
  // wrap mỗi item trong PermissionGate (Menu items không support fragment children).
  const visibleEntries = menuEntries.filter((entry) => {
    if (entry.hidden) return false;
    if (!entry.perm) return true;
    return hasAction(user, entry.perm);
  });

  const menuItems = visibleEntries.map((entry) => ({
    key: entry.key,
    icon: entry.icon,
    label: <Link to={entry.key}>{entry.label}</Link>,
  }));

  // selectedKeys: longest matching prefix → highlight đúng khi vào sub-route
  const selectedKey =
    visibleEntries
      .map((e) => e.key)
      .filter((k) => location.pathname === k || location.pathname.startsWith(k + "/"))
      .sort((a, b) => b.length - a.length)[0] ?? "/admin";

  const userMenuItems = [
    {
      key: "back-app",
      label: "Quay về trang chính",
      icon: <HomeOutlined />,
      onClick: () => navigate("/"),
    },
    { type: "divider" as const },
    {
      key: "logout",
      label: "Đăng xuất",
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        theme="dark"
        width={220}
      >
        <div
          style={{
            height: 56,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontWeight: 700,
            fontSize: 16,
            letterSpacing: 1,
          }}
        >
          {collapsed ? "E" : "E-learning 2026 Admin"}
        </div>
        <Menu theme="dark" mode="inline" selectedKeys={[selectedKey]} items={menuItems} />
      </Sider>
      <Layout>
        <Header
          style={{
            background: "#fff",
            padding: "0 24px",
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: 16 }}
          />
          <div style={{ flex: 1 }} />
          <div style={{ marginRight: 16 }}>
            <NotificationBell />
          </div>
          {/* Slot cho future "Bạn đang admin tenant X" badge */}
          <PermissionGate perm="audit.read">
            <Text type="secondary" style={{ marginRight: 16 }}>
              Quản trị viên
            </Text>
          </PermissionGate>
          {user && (
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: "pointer" }}>
                <Avatar icon={<UserOutlined />} />
                <Text>{user.email}</Text>
              </Space>
            </Dropdown>
          )}
        </Header>
        <Content style={{ padding: 24, background: "#f4f5f7" }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
